import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import './RiddledInGuessCells.css';

const sanitizeGuess = ({ raw, maxLength }) => {
  const lettersOnly = String(raw || '').replace(/[^a-zA-Z]/g, '');
  return lettersOnly.slice(0, Math.max(0, maxLength)).toUpperCase();
};

/**
 * A "cell" style input (Wordle-like).
 *
 * Uses a real <input> for mobile keyboard + accessibility, but renders
 * the value as square cells.
 */
const RiddledInGuessCells = forwardRef(
  (
    {
      value,
      onChange,
      onSubmit,
      maxLength = 0,
      disabled = false,
      keepFocused = false,
      autoFocus = false,
      compact = false,
      placeholderChar = '',
      className = '',
    },
    forwardedRef,
  ) => {
    const internalRef = useRef(null);
    const inputRef = forwardedRef || internalRef;

    const letters = useMemo(() => {
      const v = sanitizeGuess({ raw: value, maxLength });
      return v.split('');
    }, [value, maxLength]);

    const handleFocus = useCallback(() => {
      if (disabled) return;
      inputRef?.current?.focus?.();
    }, [disabled, inputRef]);

    useEffect(() => {
      if (!autoFocus || disabled) return undefined;
      const timeoutId = window.setTimeout(() => {
        inputRef?.current?.focus?.();
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }, [autoFocus, disabled, inputRef]);

    const handleBlur = useCallback(() => {
      if (!keepFocused) return;
      if (disabled) return;
      // Re-focus after the current event finishes (so button clicks still work).
      setTimeout(() => {
        inputRef?.current?.focus?.();
      }, 0);
    }, [disabled, inputRef, keepFocused]);

    const handleChange = useCallback(
      (e) => {
        const next = sanitizeGuess({ raw: e.target.value, maxLength });
        onChange?.(next);
      },
      [onChange, maxLength],
    );

    const handleKeyDown = useCallback(
      (e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          onSubmit?.();
        }
      },
      [disabled, onSubmit],
    );

    const activeIndex = Math.min(letters.length, Math.max(0, maxLength - 1));

    return (
      <div
        className={`riddled-in-cells ${compact ? 'riddled-in-cells--compact' : ''} ${
          disabled ? 'is-disabled' : ''
        } ${className}`}
        role="button"
        tabIndex={0}
        onMouseDown={(e) => {
          // Prevent focus from moving elsewhere (helps keep typing consistent).
          e.preventDefault();
          handleFocus();
        }}
        onTouchStart={() => {
          handleFocus();
        }}
        onClick={handleFocus}
        onKeyDown={(e) => {
          // Space/Enter on the wrapper should focus the input, not scroll the page.
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleFocus();
          }
        }}
        aria-label="Guess input"
      >
        {/* Real input for keyboard / screen readers (visually hidden) */}
        <input
          ref={inputRef}
          className="riddled-in-cells-input"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck="false"
          autoFocus={autoFocus}
          value={sanitizeGuess({ raw: value, maxLength })}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label="Type your guess"
        />

        <div
          className="riddled-in-cells-grid"
          style={{
            gridTemplateColumns: compact
              ? `repeat(${Math.max(1, maxLength)}, minmax(0, var(--riddled-in-cell-size, 54px)))`
              : `repeat(${Math.max(1, maxLength)}, 1fr)`,
          }}
        >
          {Array(Math.max(1, maxLength))
            .fill(null)
            .map((_, i) => {
              const letter = letters[i] || '';
              const isActive =
                !disabled && i === (letters.length < maxLength ? letters.length : activeIndex);

              return (
                <div
                  key={i}
                  className={`riddled-in-cells-cell ${isActive ? 'active' : ''} ${
                    letter ? 'filled' : ''
                  }`}
                >
                  {letter || (isActive ? '_' : placeholderChar)}
                </div>
              );
            })}
        </div>
      </div>
    );
  },
);

RiddledInGuessCells.displayName = 'RiddledInGuessCells';

export default RiddledInGuessCells;

